import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import axios from "axios";
import { darken, rgba } from "polished";
import { MatIcon, Spinner, FormTextbox } from "fiver-react-ui-components";
import { toast } from "react-toastify";
import { Formik, Form } from "formik";
import WalletFastDepositSlider from "./WalletFastDepositSlider";
import fastDepositSchema from "./fastDepositSchema";
const StyledWrapper = styled.div`
  position: relative;
  overflow: hidden;
  padding: 0;
  margin: 0 1rem 1rem;
  border-radius: ${p => p.theme.borderRadius};
  box-shadow: ${p => p.theme.boxShadowCard || "none"};
  transition: background 0.25s;
  background: ${props => props.isError ? props.theme.colorDanger : props.step === 2 ? "white" : props.theme.colorSuccess};
  color: ${p => p.step === 2 ? p.theme.colorText : p.theme.colorSuccessText};
`;
const StyledSliderWrapper = styled.div`
  flex: 1 1 50%;
  padding-left: 0.5rem;
  padding-right: 1.5rem;
`;
const StyledButton = styled.div`
  flex: 1 1 60%;
  max-width: 60%;
  min-width: 60%;
  width: 60%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  background: ${p => darken(0.1, p.theme.colorSuccess)};
  border-radius: ${p => p.theme.borderRadius};
  cursor: pointer;
`;
const StyledStep1 = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  padding-left: 1rem;
  transform: translateY(${props => props.step === 1 ? "0" : "100%"});
  transition: transform 0.25s;
`;
const StyledStep2 = styled.div`
  position: absolute;
  z-index: 3;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding-left: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transform: translateY(${props => props.step === 1 ? "-100%" : "0"});
  transition: transform 0.25s;
`;
const StyledPassword = styled.input`
  flex: 1 1 auto;
  background: transparent;
  border: 0;
  border-bottom: 2px solid ${p => p.theme.colorBorder};
  margin-right: 0.75rem;
  min-width: 1px;
  line-height: 40px;
  font-size: 1rem;
  transform: translateX(${props => props.step === 3 ? "-100%" : "0"});
  transition: transform 0.25s;

  &::placeholder {
    color: ${p => p.theme.colorLabel};
  }

  &:focus {
    border-color: ${p => darken(0.1, p.theme.colorBorder)};
  }
`;
const StyledDepositButton = styled.button`
  background: ${p => p.theme.colorSuccess};
  padding: 0 1rem;
  height: 2.5rem;
  margin-left: 0.75rem;
  margin-right: 0.75rem;
  min-width: 150px;
  font-size: 0.875rem;
  border-radius: ${p => p.theme.inputBorderRadius};
  color: ${p => p.theme.colorSuccessText};
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    background: ${p => p.theme.colorDisabledBackground};
    color: ${p => p.theme.colorDisabledText};
  }
`;
const StyledFormTextbox = styled(FormTextbox)`
  height: 2.35rem;
`;
const StyledCancelButton = styled.button`
  padding: 0;

  & > span {
    color: ${p => p.theme.colorLabel};
    font-size: 30px;
  }

  margin-right: 0.5rem;
`;
const StyledFastDeposit = styled.div`
  font-weight: 600;
  font-size: 0.75rem;
  text-align: left;
  margin: 0 0.5rem 0 1rem;
  white-space: nowrap;
`;
const StyledDepositValue = styled.div`
  display: flex;
  align-items: flex-start;
  font-size: 1.375rem;
  margin-right: 0.5rem;
`;
const StyledArrowIcon = styled(MatIcon)`
  margin-right: 1rem;
`;

const FastDepositButton = ({
  onError,
  getWallet,
  translations,
  currencySymbol = "£",
  setTransactionsUpdated
}) => {
  const [state, setState] = useState({
    step: 1,
    isError: false
  });
  const {
    step,
    isError
  } = state;

  const nextStep = e => {
    e.stopPropagation();

    if (step < 2) {
      const nextStep = step + 1;
      setState({ ...state,
        step: nextStep
      });
    }
  };

  const onSubmit = async (values, {
    setSubmitting,
    resetForm
  }) => {
    try {
      await axios.post(`/wallet/deposit`, {
        password: values.password,
        amount: +values.amount * 100
      });
      setTimeout(() => {
        setTransactionsUpdated(true);
        getWallet();
        toast.success(`Deposit success`);
        window.xtremepush("tag", "Wallet.FastDeposit", JSON.stringify({
          AppName: process.env.REACT_APP_TITLE,
          amount: values.amount
        }));
        setState({ ...state,
          step: 1
        });
        setSubmitting(false);
        resetForm();
      }, 3000);
    } catch ({
      response
    }) {
      onError(response);

      if (response?.data?.errors?.password) {
        toast.error(`${translations.password} ${translations.invalid}`);
      } else if (response?.data?.message) {
        toast.error(response?.data?.message);
      } else {
        toast.error("Server Error");
      } //setState({ ...state, step: 1 });


      setSubmitting(false);
    }
  };

  return /*#__PURE__*/React.createElement(Formik, {
    validationSchema: fastDepositSchema(translations),
    onSubmit: onSubmit,
    initialValues: {
      amount: "15.00",
      password: ""
    }
  }, ({
    isSubmitting,
    isValid,
    values,
    errors,
    dirty,
    handleChange,
    handleBlur,
    setFieldValue,
    resetForm
    /* and other goodies */

  }) => /*#__PURE__*/React.createElement(StyledWrapper, {
    step: step,
    isError: isError
  }, /*#__PURE__*/React.createElement(StyledStep1, {
    step: step
  }, /*#__PURE__*/React.createElement(StyledSliderWrapper, null, /*#__PURE__*/React.createElement(WalletFastDepositSlider, {
    setFieldValue: setFieldValue,
    value: values.amount
  })), /*#__PURE__*/React.createElement(StyledButton, {
    onClick: nextStep
  }, /*#__PURE__*/React.createElement(StyledFastDeposit, null, "Fast", /*#__PURE__*/React.createElement("br", null), " Deposit"), /*#__PURE__*/React.createElement(StyledDepositValue, null, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: "60%",
      marginRight: "2px"
    }
  }, currencySymbol), values.amount), /*#__PURE__*/React.createElement(StyledArrowIcon, {
    size: "24px"
  }, "arrow_forward_ios"))), /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(StyledStep2, {
    step: step
  }, /*#__PURE__*/React.createElement(StyledCancelButton, {
    type: "button",
    onClick: () => {
      resetForm();
      setState({ ...state,
        step: 1
      });
    }
  }, /*#__PURE__*/React.createElement(MatIcon, null, "close")), step === 2 && /*#__PURE__*/React.createElement(StyledFormTextbox, {
    step: step,
    name: "password",
    isError: dirty && errors.password,
    onChange: handleChange,
    value: values.password,
    autoFocus: true,
    placeholder: "Password",
    type: "password"
  }), /*#__PURE__*/React.createElement(StyledDepositButton, {
    disabled: isSubmitting || !isValid || !dirty,
    step: step,
    isValid: isValid
  }, isSubmitting ? /*#__PURE__*/React.createElement(Spinner, {
    forDark: true,
    lineWidth: 3
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, "Deposit ", currencySymbol, values.amount))))));
};

export default FastDepositButton;